"use client";

import Drawer, {AnchorSide} from "../../Drawer";
import useModalAnimation from "../../useModalAnimation";
import {FCWithHTMLAttributes} from "@/app/types/reactTypes";
import useRouterEffect from "../../useRouterEffect";
import RACButton from "../../RACButton";
import clsMerge from "@/app/util/clsMerge";
import {useEffect, useRef} from "react";
import LinkButton from "../../LinkButton";
import {useUserInfo} from "../../contexts/userInfoContext";
import {isUnknownUserType} from "@/app/util/userUtils";
import {useSearchParams} from "next/navigation";

export interface ContactPulloutClientWrapperProps {
	buttonContent?: React.ReactNode;
	classes?: {
		triggerButton?: string;
	};
	visibleByDefault?: boolean; //https://linear.app/artofproblemsolving/issue/WEL-300/create-modification-of-homepage-url-with-form-popped-out (set to true in HeroSection called on Homepage app/page.tsx)
}

const ContactPulloutClientWrapper: FCWithHTMLAttributes<
	ContactPulloutClientWrapperProps
> = ({buttonContent, children, classes, id, visibleByDefault}) => {
	const drawerPortalRootRef = useRef<HTMLDivElement>(null);
	const [open, animationState, handlers] = useModalAnimation();
	const {userInfo} = useUserInfo();
	const isUnknownUser = isUnknownUserType(userInfo);
	useRouterEffect({onChange: handlers.closeModal});
	const searchParams = useSearchParams();

	useEffect(() => {
		if (searchParams.get("consultation") === "open" && visibleByDefault) {
			handlers.openModal();
		}
	}, [searchParams, handlers, visibleByDefault]);

	return (
		<div ref={drawerPortalRootRef}>
			{buttonContent && (
				<>
					<LinkButton
						href={"/contact-us"}
						className={clsMerge(
							"h-full",
							classes?.triggerButton,
							// Add hidden at all breakpoints >sm to ensure that
							//  the 2 buttons are never visible at the same time
							!isUnknownUser && "md:hidden lg:hidden xl:hidden 2xl:hidden"
						)}
					>
						{buttonContent}
					</LinkButton>
					<RACButton
						id={id}
						onPress={handlers.openModal}
						className={clsMerge(
							"open-contact-sidebar-button",
							"h-full md:block",
							classes?.triggerButton,
							"hidden",
							isUnknownUser && "md:hidden"
						)}
					>
						{buttonContent}
					</RACButton>
				</>
			)}
			<Drawer
				showCloseButton
				animationState={animationState}
				setAnimation={handlers.setAnimationState}
				anchorSide={AnchorSide.RIGHT}
				isOpen={open}
				onOpenChange={handlers.setIsOpen}
				UNSTABLE_portalContainer={drawerPortalRootRef.current ?? undefined}
				classes={{
					overlay: visibleByDefault ? "backdrop-blur-none" : "",
					closeButton: visibleByDefault ? "text-3xl sm:text-lg" : "", //we want a larger close button for visibleByDefault on mobile
				}}
			>
				{children}
			</Drawer>
		</div>
	);
};

export default ContactPulloutClientWrapper;
